/**
 * Made-up artwork for the pictures that stand in for files where there are none to show:
 * the first page's stage, and the dashboard lab. Enough like the thing it is — a poster, a
 * post, a deck — that it reads as a body of work rather than a row of placeholders.
 */

export type ArtKind = 'poster' | 'post' | 'story' | 'slide' | 'logo' | 'card';

/** What a drawing needs: an id to vary it by, what it is, and four colours. */
export interface ArtPiece {
  readonly id: string;
  readonly kind: ArtKind;
  readonly palette: readonly [string, string, string, string];
}

/** The artboard's proportions, width over height. */
export function ratioOf(kind: ArtKind): number {
  return kind === 'poster' ? 210 / 297 : kind === 'story' ? 9 / 16 : kind === 'slide' ? 16 / 9 : kind === 'card' ? 85 / 55 : 1;
}

/** The first artboard, drawn, as a data URL. */
export function artSvg(file: ArtPiece): string {
  const [a, b, c, d] = file.palette;
  const r = ratioOf(file.kind);
  const w = r >= 1 ? 320 : Math.round(320 * r);
  const h = r >= 1 ? Math.round(320 / r) : 320;
  const seed = [...file.id].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  const bar = (x: number, y: number, bw: number, bh: number, fill: string, rx = bh / 2) => `<rect x="${x}" y="${y}" width="${bw}" height="${bh}" rx="${rx}" fill="${fill}"/>`;
  let body = '';
  switch (file.kind) {
    case 'poster':
      body =
        `<rect width="${w}" height="${h}" fill="${b}"/>` +
        `<circle cx="${w * 0.62}" cy="${h * 0.34}" r="${w * 0.3}" fill="${a}"/>` +
        `<path d="M0 ${h * 0.62} Q ${w * 0.5} ${h * (0.5 + (seed % 5) / 50)} ${w} ${h * 0.6} V ${h} H 0 Z" fill="${c}"/>` +
        bar(w * 0.1, h * 0.74, w * 0.62, h * 0.045, b) +
        bar(w * 0.1, h * 0.82, w * 0.4, h * 0.03, d);
      break;
    case 'post':
      body =
        `<rect width="${w}" height="${h}" fill="${a}"/>` +
        `<rect x="${w * 0.12}" y="${h * 0.12}" width="${w * 0.76}" height="${h * 0.76}" rx="${w * 0.06}" fill="${b}"/>` +
        `<circle cx="${w * 0.5}" cy="${h * 0.44}" r="${w * 0.17}" fill="${d}"/>` +
        bar(w * 0.26, h * 0.68, w * 0.48, h * 0.05, c) +
        bar(w * 0.36, h * 0.76, w * 0.28, h * 0.035, a);
      break;
    case 'story':
      body =
        `<defs><linearGradient id="s${file.id}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${a}"/><stop offset="1" stop-color="${c}"/></linearGradient></defs>` +
        `<rect width="${w}" height="${h}" fill="url(#s${file.id})"/>` +
        `<circle cx="${w * 0.3}" cy="${h * 0.3}" r="${w * 0.22}" fill="${d}" opacity="0.9"/>` +
        `<circle cx="${w * 0.72}" cy="${h * 0.48}" r="${w * 0.16}" fill="${b}" opacity="0.8"/>` +
        bar(w * 0.12, h * 0.72, w * 0.76, h * 0.04, b) +
        bar(w * 0.12, h * 0.79, w * 0.5, h * 0.025, b);
      break;
    case 'slide':
      body =
        `<rect width="${w}" height="${h}" fill="${c}"/>` +
        bar(w * 0.07, h * 0.14, w * 0.42, h * 0.08, b, 3) +
        bar(w * 0.07, h * 0.27, w * 0.3, h * 0.04, d, 2) +
        [0.35, 0.55, 0.45, 0.75, 0.62].map((t, i) => `<rect x="${w * (0.56 + i * 0.075)}" y="${h * (0.84 - t * 0.6)}" width="${w * 0.05}" height="${h * t * 0.6}" rx="3" fill="${i === 3 ? d : a}"/>`).join('') +
        bar(w * 0.07, h * 0.72, w * 0.36, h * 0.03, a, 2);
      break;
    case 'logo':
      body =
        `<rect width="${w}" height="${h}" fill="${b}"/>` +
        `<g transform="translate(${w / 2} ${h / 2})">` +
        (seed % 2 === 0
          ? `<circle r="${w * 0.2}" fill="${a}"/><circle r="${w * 0.1}" cx="${w * 0.08}" fill="${b}"/>`
          : `<rect x="${-w * 0.18}" y="${-w * 0.18}" width="${w * 0.36}" height="${w * 0.36}" rx="${w * 0.08}" fill="${a}" transform="rotate(45)"/><circle r="${w * 0.07}" fill="${d}"/>`) +
        `</g>` +
        bar(w * 0.3, h * 0.8, w * 0.4, h * 0.035, c);
      break;
    case 'card':
      body =
        `<rect width="${w}" height="${h}" fill="${b}"/>` +
        `<rect width="${w * 0.36}" height="${h}" fill="${a}"/>` +
        `<circle cx="${w * 0.18}" cy="${h * 0.5}" r="${h * 0.16}" fill="${b}"/>` +
        bar(w * 0.46, h * 0.36, w * 0.4, h * 0.08, c) +
        bar(w * 0.46, h * 0.52, w * 0.28, h * 0.05, d) +
        bar(w * 0.46, h * 0.63, w * 0.34, h * 0.05, c);
      break;
  }
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`)}`;
}

/** The five pieces the first page fans out, centre last-in. */
export const STAGE_ART: readonly ArtPiece[] = [
  { id: 'f1', kind: 'poster', palette: ['#ff7a45', '#ffd166', '#1d3557', '#fff4e0'] },
  { id: 'f4', kind: 'logo', palette: ['#7c3aed', '#ede9fe', '#1e1b4b', '#f472b6'] },
  { id: 'f7', kind: 'slide', palette: ['#111827', '#f472b6', '#22d3ee', '#fbbf24'] },
  { id: 'f2', kind: 'post', palette: ['#fb7185', '#fff1f2', '#4c0519', '#fcd34d'] },
  { id: 'f3', kind: 'story', palette: ['#0ea5e9', '#e0f2fe', '#0c4a6e', '#fde68a'] },
];
